/**
 * designTokens — the Product Visual System V2 shared token registry
 * (FULL-0155 / PROD-UX-069, MASTER_FLOW §14C.1 + §14C.5).
 *
 * One presentation vocabulary for the operator surfaces, kept deliberately small:
 *
 *   - operational typography — caption / meta / body / heading / title, plus the mono and
 *     tabular-number faces used for identifiers and counters;
 *   - spacing — the 4px scale the existing surfaces already use;
 *   - density — compact / default / comfortable row, control and gap values;
 *   - critical-state distinctions — information, recommendation, warning, approval-required,
 *     approved, destructive, blocked, verified, unknown and no-permission;
 *   - role-oriented workspaces — the five surfaces §14C.5 names;
 *   - accessibility — the 44px touch target, the visible keyboard focus ring and the skip link
 *     (PROD-UX-076 / FULL-0162);
 *   - responsive layout — the three viewport tiers and the labelled-card table
 *     (PROD-UX-077 / FULL-0163).
 *
 * The values live in `frontend/src/styles/designTokens.css`; this module is the addressable
 * registry the components and tests read. `designTokens.test.js` parses that CSS and asserts every
 * variable declared here exists there, and that every critical-state colour equals the Tailwind
 * palette value used by the canonical `statusVocabulary` arm it borrows.
 *
 * AUTHORITY. These tokens and the components built on them are presentation only. A role surface
 * or a critical-state badge never grants a permission, a role or an action. The `permission` prop
 * on `RoleSurface` can only HIDE; the backend and the existing permission context remain the
 * authority. `ROLE_SURFACE_AUTHORITY` records that in machine-readable form so it cannot be
 * mistaken for a policy layer.
 */
import { LIVE_STATUS_CLS } from "../statusVocabulary";

/** The CSS file that owns the concrete values for every token below. */
export const TOKEN_SOURCE = "frontend/src/styles/designTokens.css";

// ── Operational typography ───────────────────────────────────────────────────

export const TYPOGRAPHY_TOKENS = Object.freeze({
  fontSans: "--prodigy-font-sans",
  fontMono: "--prodigy-font-mono",
  captionSize: "--prodigy-text-operational-caption",
  metaSize: "--prodigy-text-operational-meta",
  bodySize: "--prodigy-text-operational-body",
  headingSize: "--prodigy-text-operational-heading",
  titleSize: "--prodigy-text-operational-title",
  captionLeading: "--prodigy-leading-operational-caption",
  bodyLeading: "--prodigy-leading-operational",
  weightRegular: "--prodigy-weight-regular",
  weightMedium: "--prodigy-weight-medium",
  weightSemibold: "--prodigy-weight-semibold",
  weightBold: "--prodigy-weight-bold",
  textPrimary: "--prodigy-color-text-primary",
  textSecondary: "--prodigy-color-text-secondary",
  textMuted: "--prodigy-color-text-muted",
  // The operational number face: counters, elapsed times and ids stay aligned in a column.
  numeric: "--prodigy-font-variant-numeric",
});

/** The four operational type roles a surface may apply. */
export const OPERATIONAL_TYPE_ROLES = Object.freeze({
  caption: { size: TYPOGRAPHY_TOKENS.captionSize, leading: TYPOGRAPHY_TOKENS.captionLeading },
  meta: { size: TYPOGRAPHY_TOKENS.metaSize, leading: TYPOGRAPHY_TOKENS.captionLeading },
  body: { size: TYPOGRAPHY_TOKENS.bodySize, leading: TYPOGRAPHY_TOKENS.bodyLeading },
  heading: { size: TYPOGRAPHY_TOKENS.headingSize, leading: TYPOGRAPHY_TOKENS.bodyLeading },
  title: { size: TYPOGRAPHY_TOKENS.titleSize, leading: TYPOGRAPHY_TOKENS.bodyLeading },
});

// ── Spacing (4px base) ───────────────────────────────────────────────────────

export const SPACING_TOKENS = Object.freeze({
  "0-5": "--prodigy-space-0-5",
  1: "--prodigy-space-1",
  "1-5": "--prodigy-space-1-5",
  2: "--prodigy-space-2",
  3: "--prodigy-space-3",
  4: "--prodigy-space-4",
  5: "--prodigy-space-5",
  6: "--prodigy-space-6",
  8: "--prodigy-space-8",
});

// ── Density ──────────────────────────────────────────────────────────────────

export const DENSITY_ATTRIBUTE = "data-density";
export const DEFAULT_DENSITY = "default";

/**
 * The three densities differ in row padding, control height, section gap and the reading size used
 * by dense operational tables. They are attributes (`data-density`) rather than class forks so a
 * surface can change density without re-rendering a second component tree.
 */
export const DENSITY_MODES = Object.freeze({
  compact: Object.freeze({
    key: "compact",
    rowPaddingBlock: "--prodigy-density-row-padding-block",
    controlHeight: "--prodigy-density-control-height",
    gap: "--prodigy-density-gap",
    fontSize: "--prodigy-density-font-size",
  }),
  default: Object.freeze({
    key: "default",
    rowPaddingBlock: "--prodigy-density-row-padding-block",
    controlHeight: "--prodigy-density-control-height",
    gap: "--prodigy-density-gap",
    fontSize: "--prodigy-density-font-size",
  }),
  comfortable: Object.freeze({
    key: "comfortable",
    rowPaddingBlock: "--prodigy-density-row-padding-block",
    controlHeight: "--prodigy-density-control-height",
    gap: "--prodigy-density-gap",
    fontSize: "--prodigy-density-font-size",
  }),
});

export const DENSITY_KEYS = Object.freeze(Object.keys(DENSITY_MODES));

// ── Role-oriented surfaces (MASTER_FLOW §14C.5) ──────────────────────────────

export const ROLE_SURFACE_TOKENS = Object.freeze({
  maxWidth: "--prodigy-role-surface-max-width",
  maxWidthNarrow: "--prodigy-role-surface-max-width-narrow",
  paddingInline: "--prodigy-role-surface-padding-inline",
  paddingBlock: "--prodigy-role-surface-padding-block",
  gap: "--prodigy-role-surface-gap",
  headerGap: "--prodigy-role-surface-header-gap",
});

/**
 * The five workspaces §14C.5 requires the product to distinguish. These are presentation labels,
 * not access roles: a workspace title never grants access to the routes or actions inside it.
 */
export const ROLE_WORKSPACES = Object.freeze({
  technician: Object.freeze({ key: "technician", label: "סביבת טכנאי", flowRef: "§14C.5" }),
  manager: Object.freeze({ key: "manager", label: "סביבת מנהל/מפקח", flowRef: "§14C.5" }),
  digital_employee: Object.freeze({ key: "digital_employee", label: "סביבת עובד דיגיטלי", flowRef: "§14C.5" }),
  command_center: Object.freeze({ key: "command_center", label: "מרכז בקרה", flowRef: "§14C.5" }),
  admin: Object.freeze({ key: "admin", label: "ניהול והגדרות", flowRef: "§14C.5" }),
});

export const ROLE_WORKSPACE_KEYS = Object.freeze(Object.keys(ROLE_WORKSPACES));

/**
 * Machine-readable statement of what a role surface may do. Everything except presentation and
 * hiding is false; the values are frozen and asserted by `designTokens.test.js`.
 */
export const ROLE_SURFACE_AUTHORITY = Object.freeze({
  presentationOnly: true,
  grantsRole: false,
  grantsPermission: false,
  authorizesAction: false,
  hidesWhenNotPermitted: true,
  backendAuthoritative: true,
});

/** The workspace metadata for a known role, or null for anything outside the scale. */
export function roleWorkspaceMeta(key) {
  if (typeof key !== "string") return null;
  return Object.prototype.hasOwnProperty.call(ROLE_WORKSPACES, key) ? ROLE_WORKSPACES[key] : null;
}

// ── Responsive layout (PROD-UX-077 / FULL-0163) ──────────────────────────────

/**
 * The viewport tiers the operator surfaces branch on. `useViewportTier` reads the same numbers at
 * runtime; `frontend/src/styles/responsive.css` owns the one media query (`max-width: 639px`).
 *
 * `mobileMax` is inclusive: 639px and below renders the stacked card table. 1024px is the `lg`
 * breakpoint the TicketsPage two-column layout already switches on.
 */
export const VIEWPORT_BREAKPOINTS = Object.freeze({
  mobileMax: 639,
  tabletMin: 640,
  desktopMin: 1024,
});

/** The class that turns a table into labelled cards below the mobile breakpoint. */
export const RESPONSIVE_TABLE_CLS = "prodigy-responsive-table";
/** The wrapper that stops a card-mode table from scrolling sideways. */
export const RESPONSIVE_SHELL_CLS = "prodigy-responsive-shell";
/** A horizontally scrollable table wrapper that is also a keyboard focus stop. */
export const SCROLL_REGION_CLS = "prodigy-scroll-region";

/**
 * Machine-readable statement of what the responsive layout may do. It moves presentation, never
 * facts: card mode prints a label for every cell instead of dropping columns, and no tier grants
 * a permission or enables a control the server would refuse.
 */
export const RESPONSIVE_AUTHORITY = Object.freeze({
  presentationOnly: true,
  grantsPermission: false,
  authorizesAction: false,
  keepsEveryCellInCardMode: true,
  addsCellLabelsInCardMode: true,
  hidesData: false,
});

// ── Accessibility (PROD-UX-076 / FULL-0162) ───────────────────────────────────

/**
 * The three accessibility primitives every critical operator workflow shares. They are
 * presentation only: a larger hit area or a visible focus ring makes an existing control easier to
 * reach with a keyboard or a finger; it never enables a control the server would refuse.
 *
 * `TOUCH_TARGET_MIN` is 44 CSS pixels, the WCAG 2.5.5 / platform floor. The class expands the
 * *hit area* with a centred pseudo-element rather than growing every compact operator control, so
 * dense tables keep their density while remaining touch-reachable.
 */
export const ACCESSIBILITY_TOKENS = Object.freeze({
  touchTargetMin: "--prodigy-touch-target-min",
  focusRingWidth: "--prodigy-focus-ring-width",
  focusRingOffset: "--prodigy-focus-ring-offset",
  focusRingColor: "--prodigy-focus-ring-color",
});

export const TOUCH_TARGET_PX = 44;
export const TOUCH_TARGET_CLS = "prodigy-touch-target";
export const FOCUS_RING_CLS = "prodigy-focus-ring";
export const SKIP_LINK_CLS = "prodigy-skip-link";

/**
 * Machine-readable statement of what these utilities may do. Mirrors `ROLE_SURFACE_AUTHORITY` so a
 * future reader cannot mistake the accessibility layer for a visibility or authorization layer.
 */
export const ACCESSIBILITY_AUTHORITY = Object.freeze({
  presentationOnly: true,
  grantsPermission: false,
  authorizesAction: false,
  revealsHiddenControl: false,
  enlargesHitArea: true,
  addsVisibleFocus: true,
});

// ── Critical-state distinctions (§14C.1) ─────────────────────────────────────

const state = (key, label, meaning, tone, extra = {}) =>
  Object.freeze({
    key,
    label,
    meaning,
    tone: Object.freeze(tone),
    cssVarPrefix: `--prodigy-state-${key.replace(/_/g, "-")}`,
    borderStyle: "solid",
    // The canonical statusVocabulary arm whose tone this state borrows, when the two are the same
    // fact. Information/recommendation/warning/destructive are control semantics, not stored
    // statuses, so they own their tone directly instead of pretending to be a status arm.
    toneOwner: null,
    ...extra,
  });

/**
 * Ten critical distinctions. The first five are the ones §14C.1 calls out explicitly
 * ("information, recommendation, warning, approval-required and destructive/actionable"); the
 * remaining five keep the operational states the product already drew distinct from them.
 *
 * `approved` was added by PROD-UX-070: the action spine has three separate facts after a proposal —
 * a human approved it, the executor ran it, and a postcondition was measured. `approval_required`
 * covers the first, `verified` covers only the third, and an approved-but-not-yet-verified action
 * had no truthful arm. It must not borrow `verified` (nothing was measured) or
 * `approval_required` (the approval already happened).
 *
 * `toneOwner` is checked against `LIVE_STATUS_CLS`: when set, the token triple must equal the
 * Tailwind palette classes that canonical arm already renders, so this table cannot drift into a
 * second palette.
 */
export const CRITICAL_STATES = Object.freeze({
  information: state(
    "information",
    "מידע",
    "הקשר ניטרלי — אינו המלצה ואינו דורש אישור",
    { background: "slate-100", foreground: "slate-700", border: "slate-200" }
  ),
  recommendation: state(
    "recommendation",
    "המלצה",
    "הצעה שניתנת לבחירה; אינה אישור ואינה ביצוע",
    { background: "violet-100", foreground: "violet-800", border: "violet-200" }
  ),
  warning: state(
    "warning",
    "אזהרה",
    "מידע חלקי, מיושן או לא ודאי — יש לבדוק לפני פעולה",
    { background: "amber-100", foreground: "amber-800", border: "amber-200" }
  ),
  approval_required: state(
    "approval_required",
    "דורש אישור",
    "הפעולה מותנית באישור אנושי; הבקשה אינה הרשאה לבצע",
    { background: "blue-100", foreground: "blue-800", border: "blue-200" },
    { toneOwner: "approval_required" }
  ),
  approved: state(
    "approved",
    "אושר",
    "אדם עם הרשאה אישר את הפעולה; הביצוע והתוצאה טרם אומתו",
    { background: "sky-100", foreground: "sky-800", border: "sky-200" }
  ),
  destructive: state(
    "destructive",
    "פעולה הרסנית",
    "פעולה בעלת השפעה בלתי הפיכה או מסוכנת — דורשת בקרה אנושית",
    { background: "rose-100", foreground: "rose-800", border: "rose-200" }
  ),
  blocked: state(
    "blocked",
    "חסום",
    "העבודה או הפעולה אינן יכולות להתקדם",
    { background: "red-100", foreground: "red-800", border: "red-200" },
    { toneOwner: "blocked" }
  ),
  verified: state(
    "verified",
    "מאומת",
    "נמדדה עדות להצלחה — לא הסתברות ולא הצהרה של המודל",
    { background: "emerald-50", foreground: "emerald-800", border: "emerald-200" },
    { toneOwner: "completed" }
  ),
  unknown: state(
    "unknown",
    "לא ידוע",
    "המקור לא נטען או אינו קריא — אין הצגת מצב מומצא",
    { background: "gray-100", foreground: "gray-500", border: "gray-300" },
    { toneOwner: "unknown", borderStyle: "dashed" }
  ),
  no_permission: state(
    "no_permission",
    "אין הרשאה",
    "המשטח הוסתר או אינו זמין למשתמש; ההרשאה נקבעת בשרת",
    { background: "gray-100", foreground: "gray-500", border: "gray-300" },
    { toneOwner: "no_permission", borderStyle: "dashed" }
  ),
});

export const CRITICAL_STATE_KEYS = Object.freeze(Object.keys(CRITICAL_STATES));

/** The five distinctions §14C.1 requires the visual system to keep apart. */
export const REQUIRED_CRITICAL_DISTINCTIONS = Object.freeze([
  "information",
  "recommendation",
  "warning",
  "approval_required",
  "destructive",
]);

/**
 * Metadata for a critical state, with an explicit fail-closed fallback. A key this build does not
 * know renders as `unknown`; it never guesses a more specific (and potentially reassuring) state.
 */
export function criticalStateMeta(key) {
  if (typeof key === "string" && Object.prototype.hasOwnProperty.call(CRITICAL_STATES, key)) {
    return CRITICAL_STATES[key];
  }
  return CRITICAL_STATES.unknown;
}

/** True when the value is a critical state this build can present. */
export const isKnownCriticalState = (key) =>
  typeof key === "string" && Object.prototype.hasOwnProperty.call(CRITICAL_STATES, key);

/**
 * The canonical class string this critical state borrows, if any. `null` for the control semantics
 * that are not stored statuses. Exposed so a caller never has to guess which arm owns the tone.
 */
export function criticalStateCanonicalClass(key) {
  const meta = criticalStateMeta(key);
  return meta.toneOwner ? LIVE_STATUS_CLS[meta.toneOwner] : null;
}
